import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Lock, Mail, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cleanInput } from '@/lib/utils';
import { APP_VERSION } from '@/config/envConfig';
import { authApi } from '@/services/api/auth';
import { MICROSOFT_PENDING_EMAIL_KEY } from '@/contexts/AuthContext';

type LoginStep = 'email' | 'password';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const LoginForm = () => {
  const [loginStep, setLoginStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const { toast } = useToast();

  const isEmailValid = emailPattern.test(email);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const cleanedEmail = cleanInput(email);
    if (!emailPattern.test(cleanedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsCheckingEmail(true);
    try {
      const { isOutlookAuth } = await authApi.checkLoginMethod(cleanedEmail);

      if (isOutlookAuth) {
        localStorage.setItem(MICROSOFT_PENDING_EMAIL_KEY, cleanedEmail);
        const { authorizeUrl } = await authApi.getMicrosoftLoginUrl(cleanedEmail);

        if (!authorizeUrl) {
          setError('Failed to initiate Microsoft authentication. Please try again.');
          return;
        }

        window.location.href = authorizeUrl;
        return;
      }

      setEmail(cleanedEmail);
      setLoginStep('password');
    } catch {
      setError('Failed to verify login method. Please try again.');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleUseDifferentEmail = () => {
    setLoginStep('email');
    setPassword('');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const cleanedEmail = cleanInput(email);
    const cleanedPassword = cleanInput(password);

    try {
        await login(cleanedEmail, cleanedPassword);
        toast({
            title: "Success",
            description: "Welcome back!",
            duration: 1000,
        });
    } catch (error: any) {
        if (error.response?.data?.errors) {
            const errors = error.response.data.errors;
            // First element is toast title, second element is toast description and login form error
            const errorTitle = errors[0]?.trim() || "Authentication failed";
            const errorDescription = errors[1]?.trim() || "Please check your credentials.";

            setError(errorDescription); // Show second element in login form
            toast({
                title: errorTitle, // "User authentication failed. " (first element)
                description: errorDescription, // "Email or password is incorrect" (second element)
                variant: "destructive",
                duration: 1000,
            });
        } else {
            // Fallback for network errors or malformed responses
            setError(error.message || "Login failed. Try again later.");
            toast({
                title: "Error",
                description: error.message || "An unknown error occurred.",
                variant: "destructive",
                duration: 1000,
            });
        }
    } finally {
        setIsLoading(false);
    }
};

  return (
    <main className="min-h-screen flex bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Left Side - Illustration */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="max-w-md text-center">
          <div className="mb-8">
            <img
              src="/lovable-uploads/6823d161-b5be-421b-863a-12eaf2ea796e.png"
              alt="HBox Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">PEP</h1>
            <p className="text-gray-600 text-lg">{APP_VERSION}</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/lovable-uploads/6823d161-b5be-421b-863a-12eaf2ea796e.png"
              alt="HBox Logo"
              className="h-12 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900">PEP</h1>
          </div>


          <Card className="w-full shadow-xl border-0 bg-white/95 backdrop-blur-sm">
            <CardHeader className="space-y-1 text-center pb-6">
              <CardTitle as="h1" className="text-2xl font-bold text-gray-900">Welcome Back</CardTitle>
              <CardDescription className="text-gray-600">
                Please enter your details
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loginStep === 'email' ? (
                <form onSubmit={handleEmailSubmit} className="space-y-6">
                  {error && (
                    <Alert className="border-red-200 bg-red-50" data-testid="login-error-alert">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-800" data-testid="login-error-message">
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                        required
                        autoFocus
                        className="h-12 pl-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={isCheckingEmail}
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-lg transition-all duration-200 transform hover:scale-[1.02]"
                    disabled={isCheckingEmail || !isEmailValid}
                    data-testid="login-next-button"
                  >
                    {isCheckingEmail ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      "Next"
                    )}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <Alert className="border-red-200 bg-red-50" data-testid="login-error-alert">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-800" data-testid="login-error-message">
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <label htmlFor="email-readonly" className="text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <Input
                        id="email-readonly"
                        type="email"
                        value={email}
                        readOnly
                        className="h-12 pl-10 rounded-lg border-gray-200 bg-gray-50 cursor-default"
                        data-testid="login-email-readonly"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleUseDifferentEmail}
                      className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
                      data-testid="login-change-email-button"
                    >
                      Use a different email
                    </button>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(''); }}
                        required
                        autoFocus
                        className="h-12 pl-10 pr-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={isLoading}
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        data-testid="login-toggle-password-button"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow-lg transition-all duration-200 transform hover:scale-[1.02]"
                    disabled={isLoading}
                    data-testid="login-submit-button"
                  >
                    {isLoading ? "Signing in..." : "Submit"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
};

export default LoginForm;
